import { associateDesktopWorkspace } from '@/hooks/use-message-sync';
import { loadPinnedConversations, readPinnedConversations } from '@/lib/conversation-pins';
import type { ConversationScopes } from '@/lib/desktop-shell/conversation-scope';
import {
  conversationWorktree,
  localWorkspaceRecord,
  shownWorktree,
} from '@/lib/desktop-shell/conversation-worktree';
import type { DesktopShellStore, SyncedWorktree } from '@/lib/desktop-shell/store';
import { WORKSPACE_KEY } from '@/lib/desktop-storage-keys';
import { errorMessage } from '@/lib/error-message';
import {
  cleanupNativeGitWorktrees,
  createNativeManagedGitWorktree,
  handoffNativeGitWorktree,
  readNativeGitStatus,
  restoreNativeGitWorktree,
} from '@/lib/native-desktop';

export type WorktreeSettingsInput = {
  worktreeRoot: string;
  worktreeAutoCleanup: boolean;
  worktreeKeepCount: number;
};

export type WorktreeLifecycle = {
  /** Whether the worktree was made: on failure, the error says why. */
  prepareManagedWorktree: (sourceRoot?: string | null) => Promise<boolean>;
  handoffWorktree: (destination: 'local' | 'worktree') => Promise<void>;
};

/**
 * Brings a conversation's worktree back where the server records it: as it is when it is there,
 * restored from what its cleanup kept, else made again from its repository. Its path stays the
 * same, wherever new worktrees go now.
 */
export async function reopenConversationWorktree(
  chatId: string,
  worktree: SyncedWorktree,
): Promise<void> {
  if (worktree.sourceRoot === null) {
    await restoreNativeGitWorktree(worktree.path);
    return;
  }
  await createNativeManagedGitWorktree({
    workspaceRoot: worktree.sourceRoot,
    ownerThreadId: chatId,
    worktreePath: worktree.path,
  });
}

/**
 * Managed git worktree lifecycle for the desktop shell: create a worktree for
 * the active conversation and hand it off back to (or from) the source
 * checkout. Which worktree is a conversation's comes from the server's records
 * (`conversation-worktree.ts`): a conversation that has one reuses it, made
 * again in place once cleaned up. Each operation belongs to the conversation it
 * started in: making a worktree takes seconds, and the user may open another
 * conversation meanwhile, which keeps its own folder (`ConversationScope`).
 * Auto-cleanup keeps the worktrees of the conversations open, running, waiting
 * to send or pinned (the server's pins).
 */
export function createWorktreeLifecycle({
  store,
  conversations,
  preview,
  worktreeSettings,
}: {
  store: DesktopShellStore;
  conversations: ConversationScopes;
  preview: boolean;
  worktreeSettings: WorktreeSettingsInput;
}): WorktreeLifecycle {
  async function prepareManagedWorktree(sourceRoot?: string | null): Promise<boolean> {
    const conversation = conversations.shown();
    const { chatId } = conversation;
    const state = store.getState();
    const { workspace, workspaceEnvironment, worktreeStartingState } = state;
    // Its waiting message leaves once the worktree is made, or goes back to the composer.
    const settle = (made: boolean) =>
      conversation.update(({ pendingSend }) => ({
        creatingWorktree: false,
        ...(pendingSend?.kind === 'worktree'
          ? { pendingSend: made ? { ...pendingSend, ready: true } : null }
          : {}),
      }));
    // The one it has already (its folder may be the checkout now), else a new one of the
    // repository: a worktree of the checkout, never of another worktree.
    const recorded = conversationWorktree(state, chatId, null);
    const root =
      sourceRoot === undefined
        ? (recorded?.sourceRoot ??
          localWorkspaceRecord(state.syncedWorkspaces, workspace)?.sourceRoot ??
          workspace)
        : sourceRoot;
    if (root === null) {
      settle(false);
      return false;
    }
    store.getState().setBranchError(null);
    conversation.update({ creatingWorktree: true });
    try {
      const worktree = await createNativeManagedGitWorktree({
        workspaceRoot: root,
        ownerThreadId: chatId,
        environment: workspaceEnvironment,
        startingBranch:
          worktreeStartingState.type === 'branch'
            ? { branch: worktreeStartingState.branch, remote: worktreeStartingState.remote }
            : null,
        worktreeRoot: worktreeSettings.worktreeRoot.trim() || null,
        worktreePath: recorded?.path ?? null,
      });
      // The server records it, linked to its repository: shown again on any device, the
      // conversation opens there, filed and named under that repository.
      let path = worktree.path;
      try {
        const saved = await associateDesktopWorkspace(worktree.path, { runtime: 'worktree' });
        path = saved.rootPath;
        const kept: SyncedWorktree = { path, sourceRoot: saved.sourceRoot, available: true };
        store
          .getState()
          .setSyncedWorkspaces((current) => [
            { ...saved, availableOnDevice: true },
            ...current.filter((candidate) => candidate.id !== saved.id),
          ]);
        store
          .getState()
          .setSyncedThreads((current) =>
            current.map((entry) => (entry.id === chatId ? { ...entry, worktree: kept } : entry)),
          );
      } catch (error) {
        store.getState().setWorkspaceSyncError(errorMessage(error));
      }
      conversation.place({ workspace: path, executionMode: 'worktree' });
      settle(true);
      if (worktreeSettings.worktreeAutoCleanup) void cleanupWorktrees(root, chatId);
      return true;
    } catch (error) {
      conversation.whileShown((state) => state.setBranchError(errorMessage(error)));
      settle(false);
      return false;
    }
  }

  /**
   * Removes the oldest managed worktrees of `root` beyond the kept count. The worktree of a
   * conversation open (shown or in a split), running, waiting to send or pinned is never one.
   * The server keeps the records of those removed: their conversations make them again.
   */
  async function cleanupWorktrees(root: string, madeFor: string): Promise<void> {
    // The pins as the server has them now, which another device may have changed.
    const pinned = await loadPinnedConversations().catch(() => readPinnedConversations());
    const state = store.getState();
    const kept = new Set([
      madeFor,
      ...pinned,
      state.chatId,
      ...state.splitThreadIds,
      ...state.loadingThreadIds,
      // Those making their worktree or waiting to send.
      ...Object.entries(state.conversationStates).flatMap(([id, conversation]) =>
        conversation.creatingWorktree || conversation.pendingSend !== null ? [id] : [],
      ),
    ]);
    const removedPaths = await cleanupNativeGitWorktrees(
      root,
      [...kept],
      worktreeSettings.worktreeKeepCount,
      worktreeSettings.worktreeRoot.trim() || null,
    ).catch(() => [] as string[]);
    if (removedPaths.length === 0) return;
    const removed = new Set(removedPaths);
    store
      .getState()
      .setSyncedWorkspaces((current) =>
        current.map((entry) =>
          removed.has(entry.rootPath) ? { ...entry, availableOnDevice: false } : entry,
        ),
      );
    store
      .getState()
      .setSyncedThreads((current) =>
        current.map((entry) =>
          entry.worktree && removed.has(entry.worktree.path)
            ? { ...entry, worktree: { ...entry.worktree, available: false } }
            : entry,
        ),
      );
    // The next conversation does not start in a folder that is gone.
    if (removed.has(localStorage.getItem(WORKSPACE_KEY) ?? ''))
      localStorage.setItem(WORKSPACE_KEY, root);
  }

  async function handoffWorktree(destination: 'local' | 'worktree'): Promise<void> {
    const conversation = conversations.shown();
    const state = store.getState();
    const worktree = shownWorktree(state);
    if (worktree === null || preview || conversation.state().handingOffWorktree) return;
    state.setBranchError(null);
    conversation.update({ handingOffWorktree: true });
    try {
      if (destination === 'worktree')
        await reopenConversationWorktree(conversation.chatId, worktree);
      const result = await handoffNativeGitWorktree(worktree.path, destination);
      conversation.place({ workspace: result.path, executionMode: destination });
      const status = await readNativeGitStatus(result.path);
      conversation.whileShown((shown) => shown.setGitStatus(status));
    } catch (error) {
      conversation.whileShown((shown) => shown.setBranchError(errorMessage(error)));
    } finally {
      conversation.update({ handingOffWorktree: false });
    }
  }

  return { prepareManagedWorktree, handoffWorktree };
}
